import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import moment from 'moment';
import {
  Input,
  Select,
  DatePicker
} from 'syngenta-digital-cropwise-react-ui-kit';
import useCompanies from 'hooks/useCompanies';
import FormSection from 'components/FormSection';
import SectionWrapper from 'components/SectionWrapper';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import {
  COL_SPAN_FULL,
  COL_SPAN_ONE_THIRD
} from 'components/FormGrid/constants';
import TagsSection from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection';
import CreateNewButton from 'components/CreateNewButton';
import ChevronDownIcon from 'assets/chevron_down.svg?react';
import ChevronUpIcon from 'assets/chevron_up.svg?react';
import AddNewCompanyPeopleDrawer from 'components/CompanyPeopleDropdown/components/AddNewCompanyPeopleDrawer';
import { CompanyPeopleItem } from 'components/CompanyPeopleDropdown/types';
import alphanumSort from 'helpers/sorters';
import { InvoiceFormValues } from '../../Constants/invoiceFormikData';
import { getSelectedCropSeasonIds } from '../../helpers/breadcrumbCropSeasons';
import './style.css';

const { TextArea } = Input;
const MAX_NOTES = 1000;

const DetailsStep: React.FC = () => {
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched
  } = useFormikContext<InvoiceFormValues>();

  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);
  const [isCompanySelectOpen, setIsCompanySelectOpen] = useState(false);
  // Holds the just-created company so the select can show its name
  // immediately, without waiting on loadCompanies() to refresh the list.
  const [pendingCompany, setPendingCompany] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const {
    companies,
    loading: companiesLoading,
    loadCompanies
  } = useCompanies() as {
    companies: Array<{ id: string; name: string }>;
    loading: boolean;
    loadCompanies: () => void;
  };

  // Seed cropSeason from the current Breadcrumb selection only when the form
  // doesn't already have a value (edit/duplicate mode loads real values from
  // the fetched invoice before this step mounts).
  useEffect(() => {
    if (!values.cropSeason || values.cropSeason.length === 0) {
      const ids = getSelectedCropSeasonIds();
      if (ids.length > 0) {
        setFieldValue('cropSeason', ids);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyOptions = alphanumSort([...(companies ?? [])], 'name').map(
    (c: { id: string; name: string }) => ({
      label: c.name,
      value: c.id
    })
  );

  // Merge in the just-created company so its name resolves immediately,
  // even before loadCompanies() finishes refetching the list.
  if (
    pendingCompany &&
    !companyOptions.some(
      (option: { value: string }) => option.value === pendingCompany.id
    )
  ) {
    companyOptions.push({
      label: pendingCompany.name,
      value: pendingCompany.id
    });
  }

  const handleCompanyAdded = (newValue: CompanyPeopleItem | null) => {
    if (newValue) {
      setPendingCompany({ id: newValue.id, name: newValue.name });
      loadCompanies();
      setFieldValue('vendor', newValue.id);
    }
    setIsCreateCompanyOpen(false);
  };

  const companyDropdownRender = (menu: React.ReactElement) => (
    <div>
      {menu}
      <CreateNewButton
        onCreateNew={() => {
          // Close the select's popup immediately so it doesn't linger and
          // visually overlap the Create Company drawer as it opens.
          setIsCompanySelectOpen(false);
          setIsCreateCompanyOpen(true);
        }}
        text="Create New Company"
      />
    </div>
  );

  const invoiceDetailsRows: FormRowConfig[] = [
    {
      id: 'invoice-summary-row',
      showErrorLineConditionally: false,
      fields: [
        {
          id: 'invoiceNumber',
          label: 'Invoice Number',
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.invoiceNumber ? errors.invoiceNumber : undefined,
          render: () => (
            <Input
              type="default"
              className="invoice-number-input no-margin"
              name="invoiceNumber"
              // Focus the first field on entry to the create/edit/duplicate
              // flow (Details is step 0, so this mounts on flow entry).
              autoFocus
              value={values.invoiceNumber}
              onChange={handleChange}
              onBlur={() => setFieldTouched('invoiceNumber', true)}
              status={
                touched.invoiceNumber && errors.invoiceNumber
                  ? 'error'
                  : undefined
              }
            />
          )
        },
        {
          id: 'invoiceDate',
          label: 'Invoice Date',
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.invoiceDate
            ? String(errors.invoiceDate ?? '') || undefined
            : undefined,
          render: () => (
            <DatePicker
              style={{ width: '100%' }}
              value={values.invoiceDate ? moment(values.invoiceDate) : null}
              onChange={(date: moment.Moment | null) =>
                setFieldValue('invoiceDate', date ? date.toDate() : null)
              }
              onBlur={() => setFieldTouched('invoiceDate', true)}
              status={
                touched.invoiceDate && errors.invoiceDate ? 'error' : undefined
              }
              format="MM/DD/YYYY"
              allowClear={false}
            />
          )
        },
        {
          id: 'invoiceDueDate',
          label: 'Due Date',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <DatePicker
              style={{ width: '100%' }}
              value={
                values.invoiceDueDate ? moment(values.invoiceDueDate) : null
              }
              onChange={(date: moment.Moment | null) =>
                setFieldValue('invoiceDueDate', date ? date.toDate() : null)
              }
              onBlur={() => setFieldTouched('invoiceDueDate', true)}
              format="MM/DD/YYYY"
            />
          )
        }
      ]
    },
    {
      id: 'company-row',
      fields: [
        {
          id: 'vendor',
          label: 'Vendor',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <>
              <Select
                id="vendor"
                className="custom-select"
                popupClassName="cwf-custom-select-dropdown"
                style={{ width: '100%' }}
                showSearch
                open={isCompanySelectOpen}
                onDropdownVisibleChange={setIsCompanySelectOpen}
                filterOption={(input: string, option: any) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={
                  companiesLoading ? undefined : values.vendor || undefined
                }
                onChange={(val: string) => setFieldValue('vendor', val)}
                onBlur={() => setFieldTouched('vendor', true)}
                options={companyOptions}
                dropdownRender={companyDropdownRender}
                suffixIcon={
                  isCompanySelectOpen ? (
                    <ChevronUpIcon width={18} height={18} />
                  ) : (
                    <ChevronDownIcon width={18} height={18} />
                  )
                }
                allowClear
              />
              <AddNewCompanyPeopleDrawer
                open={isCreateCompanyOpen}
                onClose={() => setIsCreateCompanyOpen(false)}
                onCompanyPeopleAdded={handleCompanyAdded}
                title="Create New Company"
                defaultType="Company"
                disableTypeChange
              />
            </>
          )
        }
      ]
    }
  ];

  const currentNotesLength = (values.notes || '').length;
  const isOverLimit = currentNotesLength > MAX_NOTES;
  const countColor = isOverLimit ? '#CF3537' : '#696F88';

  const notesRows: FormRowConfig[] = [
    {
      id: 'notes-row',
      fields: [
        {
          id: 'notes',
          colSpan: COL_SPAN_FULL,
          render: () => (
            <div>
              <TextArea
                name="notes"
                value={values.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFieldValue('notes', e.target.value)
                }
                onBlur={() => setFieldTouched('notes', true)}
                rows={6}
                status={
                  (touched.notes && errors.notes) || isOverLimit
                    ? 'error'
                    : undefined
                }
                style={{ width: '100%', resize: 'none', minHeight: '200px' }}
                autoSize={false}
                aria-label="Invoice notes"
              />
              <div
                className="text-right text-xs"
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: countColor,
                  marginTop: '4px'
                }}
              >
                {currentNotesLength.toLocaleString()} /{' '}
                {MAX_NOTES.toLocaleString()} characters
              </div>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <div className="flex flex-col w-full pb-4">
      <SectionWrapper isFirst>
        <FormSection title="INVOICE INFO" autoHeight hasMarginBottom>
          <FormGrid rows={invoiceDetailsRows} showErrorLineConditionally />
        </FormSection>
      </SectionWrapper>

      <SectionWrapper>
        <FormSection title="TAGS" isOptional autoHeight>
          <TagsSection />
        </FormSection>
      </SectionWrapper>

      <SectionWrapper>
        <FormSection
          title="NOTES"
          isOptional
          bodyHeight={248}
          hasMarginBottom={false}
        >
          <FormGrid rows={notesRows} showErrorLineConditionally />
        </FormSection>
      </SectionWrapper>
    </div>
  );
};

export default DetailsStep;
